import { useCallback, useMemo } from "react";
import {
	DataFieldValue,
	DataFieldValues,
	RegistrationFormSectionResourceTypeCompanionSection,
	RegistrationGuestAttribute,
	RuleGuestAttributeResourceTypeDataField,
	RuleResource,
} from "../../../shared/types";
import { CurrentCompanion, useRegistrationForm } from "../../provider/registrationFormProviderContext";
import { toDataFieldRequestValues } from "../../provider/registrationFormProviderUtils";
import { createInitialValues, toGuestAttributeChildrenFromSection } from "../../registrationFormUtils";

export const useRegistrationCompanionForm = (
	section: RegistrationFormSectionResourceTypeCompanionSection,
	currentCompanion: CurrentCompanion,
) => {
	const { saveCompanion } = useRegistrationForm();

	const guestAttributes = useMemo(() => toGuestAttributeChildrenFromSection(section), [section]);
	const dataFields = useMemo(() => guestAttributes.map((attribute) => attribute.dataField), [guestAttributes]);

	const initialValues = useMemo(() => {
		const companionGuestAttributeValues: RegistrationGuestAttribute[] = currentCompanion?.companion.dataFieldValues.map(
			(dataFieldValue) => {
				const guestAttributeId =
					guestAttributes.find((guestAttribute) => guestAttribute.dataField.id == dataFieldValue.id)?.id ??
					dataFieldValue.id;
				return {
					id: guestAttributeId,
					value: dataFieldValue.value,
				};
			},
		);
		return createInitialValues(guestAttributes, companionGuestAttributeValues, true);
	}, [currentCompanion?.companion.dataFieldValues, guestAttributes]);

	const validationRuleMap = useMemo(
		() =>
			section.validationRules.reduce(
				(acc, cur) => ({
					...acc,
					[cur.guestAttribute.dataField.id]: [...(acc[cur.guestAttribute.dataField.id] ?? []), cur],
				}),
				{} as Record<number, RuleResource<RuleGuestAttributeResourceTypeDataField>[]>,
			),
		[section.validationRules],
	);

	const submitCompanion = useCallback(
		(
			values: DataFieldValues,
			companionTypeId: number,
			setError: (error: Record<number, string> | null) => void,
			section: RegistrationFormSectionResourceTypeCompanionSection,
			afterSuccess?: () => void,
		) => {
			const dataFieldValues: DataFieldValue[] = dataFields.map(toDataFieldRequestValues(values, undefined, true));

			saveCompanion(
				{
					index: currentCompanion.index,
					companionType: currentCompanion.companionType,
					companion: {
						guestId: currentCompanion?.companion.guestId ?? null,
						dataFieldValues: dataFieldValues.filter((v) => v.value !== undefined),
						isEditable: currentCompanion?.companion.isEditable ?? false,
						typeId: companionTypeId,
						sectionId: section.id,
					},
				},
				setError,
				section,
			);
			afterSuccess && afterSuccess();
		},
		[currentCompanion, dataFields, saveCompanion],
	);

	return { submitCompanion, initialValues, validationRuleMap };
};
